import {
	createAsyncThunk,
	createSlice,
	nanoid,
	type Reducer,
} from '@reduxjs/toolkit'
import * as Sentry from '@sentry/browser'
import i18next from 'i18next'
import { orderBy, union } from 'lodash'
import type { CreateProjectsInput, UpdateProjectsInput } from 'src/API'
import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import { projectApi } from 'src/redux/api/graphql/projectsApi'
import { userInfoApi } from 'src/redux/api/graphql/userInfoApi'
import type { RootState, ThunkAppDispatch } from 'src/redux/store'
import type {
	GetProjectsQueryApi,
	GetProjectTotalsQueryApi,
	ReduxCompany,
	ReduxFreelancers,
	ReduxProjects,
	ReduxQuote,
	ReduxUserInfo,
} from 'src/types/apiType'
import type { ApiStatus, QuoteType, UserInfo } from 'src/types/miscType'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { getErrorMessage } from 'src/utils/error'
import {
	checkIfItemIsAccessible,
	fetchArchiveProjectsByTenantIdUtil,
	fetchProjectsByClientIdUtil,
	fetchProjectsByParentProjectIdUtil,
	fetchProjectsByTenantIdUtil,
	fetchProjectsByTenantIdUtilForSubTeamAdmin,
	fetchProjectsByTenantIdUtilForTeamAdmin,
	fetchQuotesByParentProjectIdUtil,
	getFetchFilter,
} from 'src/utils/fetchUtils'
import {
	makeErrorToast,
	makeErrorToastWithLink,
	makeSuccessToast,
	msg,
} from 'src/utils/makeToast'
import {
	delayInMs,
	getProjectNumber,
	getProjectNumberDetails,
	updateCompanyInfoWithDeltaSyncSuppress,
} from 'src/utils/miscellaneous'
import { captureExceptionAndThrow } from 'src/utils/posthogException'
import projectChildrenMergeSort from 'src/utils/projectChildrenMergeSort'
import { getProjectTotals } from 'src/utils/projectUtil'
import { tryCatch } from '@/utils/tryCatch'

interface CategoryTotals {
	isInternational: boolean
	categoryPrefix: string
	categoryRoman: string
	categoryId: number
	category: string
	category_fr: string
	budgetTotal: number
	provisionalTotal: number
	actualTotal: number
}

interface Totals {
	budgetSubTotal: number
	budgetGrandTotal: number
	budgetMargin: number
	budgetMarginPercentage: number
	provisionalSubTotal: number
	provisionalGrandTotal: number
	provisionalMargin: number
	provisionalMarginPercentage: number
	actualSubTotal: number
	actualGrandTotal: number
	actualMargin: number
	actualMarginPercentage: number
}

interface Discount {
	totalAfterDiscount: number
	totalDiscount: number
	budgetTotalDiscountOnMargin: number
	budgetTotalDiscountOnMarginPercentage: number
	budgetTotalDiscountPercentage: number
	provisionalTotalDiscountOnMargin: number
	provisionalTotalDiscountOnMarginPercentage: number
	provisionalTotalDiscountPercentage: number
	actualTotalDiscountOnMargin: number
	actualTotalDiscountOnMarginPercentage: number
	actualTotalDiscountPercentage: number
}

interface ProjectTotals {
	id: string | null
	name: string | null
	quotes?: CategoryTotals[]
	categoryTotals?: CategoryTotals[]
	// biome-ignore lint/complexity/noBannedTypes: <>
	totals?: Totals | {}
	// biome-ignore lint/complexity/noBannedTypes: <>
	franceTotals?: Totals | {}
	// biome-ignore lint/complexity/noBannedTypes: <>
	internationalTotals?: Totals | {}
	discount?: Discount | null
	franceCategoryTotals?: CategoryTotals[]
	internationalCategoryTotals?: CategoryTotals[]
}

interface ProjectState {
	allProjects: ReduxProjects[]
	projectTotalsLambda: GetProjectTotalsQueryApi['getProjectTotals'] | null
	projectData: ReduxProjects[]
	projectChildrenData: (ReduxProjects | ReduxQuote)[]
	freelanceEmail: ReduxUserInfo[]
	freelancerInfo: ReduxFreelancers | null
	projectTotals: ProjectTotals | null
	summaryQuoteId: string
	quoteType: QuoteType | null
	fetchAllProjectApiStatus: ApiStatus
	fetchProjectApiStatus: ApiStatus
	fetchProjectChildrenApiStatus: ApiStatus
	fetchProjectTotalsApiStatus: ApiStatus
	getProjectTotalsFromLambdaApiStatus: ApiStatus
	listFreelancerInfoApiStatus: ApiStatus
	fetchFreelancerInfoApiStatus: ApiStatus
	createOrUpdateProjectApiStatus: ApiStatus
	deleteProjectApiStatus: ApiStatus
	createFreelancerArchiveProjectApiStatus: ApiStatus
	updateFreelancerInfoStatus: ApiStatus
	removeFreelancerStatus: ApiStatus
	moveProjectApiStatus: ApiStatus
	isDeleteDialogOpen: boolean
	projectModalOpenStatus: boolean
	subTeamModalOpenStatus: boolean
	projectDeleteDialogOpenStatus: boolean
	subTeamDeleteDialogOpenStatus: boolean
	freelancerChargeConfirmDialogStatus: boolean
	freelancerPage: 'parent' | 'child' | null
	isFreelancerModalOpen: boolean
	projectMoveDialogOpenStatus: boolean
	currentProject: ReduxProjects | null
	error: any | null
}

const initialState: ProjectState = {
	allProjects: [],
	projectTotalsLambda: null,
	projectData: [],
	projectChildrenData: [],
	freelanceEmail: [],
	freelancerInfo: null,
	projectTotals: null,
	summaryQuoteId: '',
	quoteType: null,
	fetchAllProjectApiStatus: 'idle',
	fetchProjectApiStatus: 'idle',
	fetchProjectChildrenApiStatus: 'idle',
	fetchProjectTotalsApiStatus: 'idle',
	getProjectTotalsFromLambdaApiStatus: 'idle',
	listFreelancerInfoApiStatus: 'idle',
	fetchFreelancerInfoApiStatus: 'idle',
	createOrUpdateProjectApiStatus: 'idle',
	deleteProjectApiStatus: 'idle',
	createFreelancerArchiveProjectApiStatus: 'idle',
	updateFreelancerInfoStatus: 'idle',
	removeFreelancerStatus: 'idle',
	moveProjectApiStatus: 'idle',
	isDeleteDialogOpen: false,
	projectModalOpenStatus: false,
	subTeamModalOpenStatus: false,
	projectDeleteDialogOpenStatus: false,
	subTeamDeleteDialogOpenStatus: false,
	freelancerPage: null,
	isFreelancerModalOpen: false,
	projectMoveDialogOpenStatus: false,
	freelancerChargeConfirmDialogStatus: false,
	currentProject: null,
	error: null,
}

export const fetchAllProjects = createAsyncThunk<
	ReduxProjects[],
	undefined,
	{
		state: RootState
		rejectValue: string
	}
>('project/fetchAllProjects', async (args, { getState, rejectWithValue }) => {
	try {
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const currentCompanyTenantId = userTableInfo.currentCompanyTenantId
		const isTeamAdmin = userTableInfo.cognitoGroup?.includes('teamAdmin')
		const isSubTeamAdmin =
			userTableInfo.cognitoGroup?.includes('subTeamAdmin')
		const teamIds = userTableInfo.teamIds
		const subTeamIds = userTableInfo.subTeamIds

		let items = [] as ReduxProjects[]

		if (isTeamAdmin) {
			items = await fetchProjectsByTenantIdUtilForTeamAdmin({
				tenantId: currentCompanyTenantId,
				teamIds,
			})
		} else if (isSubTeamAdmin) {
			items = await fetchProjectsByTenantIdUtilForSubTeamAdmin({
				tenantId: currentCompanyTenantId,
				subTeamIds,
			})
		} else {
			items = await fetchProjectsByTenantIdUtil(currentCompanyTenantId)
		}

		// For any data fetched by datastore we need to stringify & then parse it for it to be serializable and used by redux

		return items
	} catch (err: any) {
		makeErrorToast(msg)
		return rejectWithValue(err)
	}
})

// Only fetches project on clients/:clientsId page only companyAdmin can view this page
export const fetchProject = createAsyncThunk<
	ReduxProjects[],
	{ parentClientId: string },
	{
		state: RootState
		rejectValue: string
	}
>(
	'project/fetchProject',
	async ({ parentClientId }, { getState, rejectWithValue }) => {
		try {
			const { auth, settings } = getState()
			const projectFilter = getFetchFilter(auth.userTableInfo)
			const sort = settings.sort
			const items: ReduxProjects[] = await fetchProjectsByClientIdUtil(
				parentClientId,
				projectFilter
			)

			// For any data fetched by datastore we need to stringify & then parse it for it to be serializable and used by redux
			let sortedProjects: ReduxProjects[] = []
			if (sort === 'date') {
				sortedProjects = orderBy(items, ['createdOn'], ['asc'])
			} else if (sort === 'dateM') {
				sortedProjects = orderBy(items, ['createdOn'], ['desc'])
			} else if (sort === 'name') {
				sortedProjects = orderBy(
					items,
					[(project) => project?.name?.toLowerCase()],
					['asc']
				)
			}
			if (sortedProjects) {
				return sortedProjects
			}
			makeErrorToast(msg)
			return rejectWithValue('Error Fetching Project')
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const fetchFreelancerProject = createAsyncThunk<
	ReduxProjects[],
	{ currentCompanyTenantId: string },
	{
		state: RootState
		rejectValue: string
	}
>(
	'project/fetchFreelancerProject',
	async ({ currentCompanyTenantId }, { getState, rejectWithValue }) => {
		const { auth, settings } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const freelancerTenantId = userTableInfo?.tenantId
		const sort = settings.sort
		if (!userTableInfo.cognitoGroup?.includes('freelancer')) {
			captureExceptionAndThrow(new Error('User is not a freelancer'), {
				location: 'projects.fetchFreelancerProject',
				userId: userTableInfo.id,
				groups: userTableInfo.cognitoGroup,
			})
		}
		try {
			const {
				data: {
					listProjects: { items },
				},
			} = (await API.graphql(
				graphqlOperation(queries.listProjects, {
					filter: {
						activeFlag: { eq: true },
						freelancers: { contains: freelancerTenantId },
						tenantId: { eq: currentCompanyTenantId },
					},
					limit: 1000,
				})
			)) as { data: { listProjects: { items: ReduxProjects[] } } }

			// For any data fetched by datastore we need to stringify & then parse it for it to be serializable and used by redux
			let sortedProjects: ReduxProjects[] = []
			if (sort === 'date') {
				sortedProjects = orderBy(items, ['createdOn'], ['asc'])
			} else if (sort === 'dateM') {
				sortedProjects = orderBy(items, ['createdOn'], ['desc'])
			} else if (sort === 'name') {
				sortedProjects = orderBy(
					items,
					[(project) => project.name?.toLowerCase()],
					['asc']
				)
			}
			if (sortedProjects) {
				return sortedProjects
			}
			makeErrorToast(msg)
			return rejectWithValue('Error Fetching Project')
		} catch (err: any) {
			console.log('err: ', err)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

// fetches project & quote children for parentProjectID from project/:projectId page both companyAdmin & freelancer can see this
export const fetchProjectChildren = createAsyncThunk<
	(ReduxProjects | ReduxQuote)[],
	{ parentProjectId: string },
	{
		state: RootState
		rejectValue: string
	}
>(
	'project/fetchProjectChildren',
	async ({ parentProjectId }, { getState, rejectWithValue }) => {
		try {
			const { auth, settings } = getState()
			const filter = getFetchFilter(auth.userTableInfo)
			const sort = settings.sort

			const projectItems: ReduxProjects[] =
				await fetchProjectsByParentProjectIdUtil(
					parentProjectId,
					filter
				)

			const quoteItems: ReduxQuote[] =
				await fetchQuotesByParentProjectIdUtil(parentProjectId, filter)

			const projectChildrenData = [...projectItems, ...quoteItems]

			let sortedProjectChildren = projectChildrenData
			if (sort === 'date') {
				sortedProjectChildren = orderBy(
					projectChildrenData,
					['createdOn'],
					['asc']
				)
			} else if (sort === 'dateM') {
				sortedProjectChildren = orderBy(
					projectChildrenData,
					['createdOn'],
					['desc']
				)
			} else if (sort === 'name') {
				sortedProjectChildren = orderBy(
					projectChildrenData,
					[(projectChild) => projectChild?.name?.toLowerCase()],
					['asc']
				)
			}
			return sortedProjectChildren
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const fetchProjectTotals = createAsyncThunk<
	ProjectTotals,
	string,
	{
		state: RootState
		rejectValue: string
	}
>(
	'project/fetchProjectTotals',
	async (projectId, { getState, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const fetchFilter = getFetchFilter(auth.userTableInfo)
			const projectTotals: ProjectTotals = await getProjectTotals({
				projectId,
				fetchFilter,
			})
			// console.log('projectTotals: ', projectTotals)
			return projectTotals
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const getProjectTotalsFromLambda = createAsyncThunk<
	GetProjectTotalsQueryApi['getProjectTotals'],
	string,
	{
		state: RootState
		rejectValue: string
	}
>(
	'project/getProjectTotalsFromLambda',
	async (projectId, { getState, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const {
				data: { getProjectTotals },
			} = (await API.graphql(
				graphqlOperation(queries.getProjectTotals, {
					projectId: projectId,
				})
			)) as {
				data: {
					getProjectTotals: GetProjectTotalsQueryApi['getProjectTotals']
				}
			}
			return getProjectTotals
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

//  Logic to get freelancer data
export const listFreelancerInfo = createAsyncThunk<
	ReduxUserInfo[],
	undefined,
	{
		state: RootState
		rejectValue: string
	}
>('project/listFreelancerInfo', async (args, { getState, rejectWithValue }) => {
	try {
		const { projects } = getState()
		const currentProject = projects.currentProject
		const freelancers = currentProject?.freelancers ?? []
		// Since we are adding dummyId for all freelancers we need to
		//  remove that from freelancers when searching for email
		const removeDummyIdFromFreelancersArray = freelancers.filter(
			(i) => i !== 'dummyId'
		)
		const freelanceEmail = [] as ReduxUserInfo[]
		// Only check further if freelancers array has other elements other than dummyId
		if (!removeDummyIdFromFreelancersArray) {
			return freelanceEmail
		}
		await Promise.all(
			removeDummyIdFromFreelancersArray.map(async (freelancersId) => {
				const {
					data: {
						userInfoByTenantId: { items: freelancerInfoArray },
					},
				} = (await API.graphql(
					graphqlOperation(queries.userInfoByTenantId, {
						tenantId: freelancersId,
					})
				)) as {
					data: { userInfoByTenantId: { items: ReduxUserInfo[] } }
				}
				// Telling typescript we have the particular nested object items
				if (
					Array.isArray(freelancerInfoArray) &&
					freelancerInfoArray?.length === 1
				) {
					freelanceEmail.push(freelancerInfoArray[0])
				}
			})
		)
		return freelanceEmail
	} catch (err: any) {
		console.log('err listFreelancerInfo: ', err)
		makeErrorToast(msg)
		return rejectWithValue(err)
	}
})

export const fetchFreelancerInfo = createAsyncThunk<
	ReduxFreelancers | null,
	string,
	{
		rejectValue: string
	}
>(
	'settings/fetchFreelancerInfo',
	async (freelancerTenantId, { rejectWithValue }) => {
		try {
			const {
				data: {
					freelancersById: { items: freelancerInfoArray },
				},
			} = (await API.graphql(
				graphqlOperation(queries.freelancersById, {
					freelancerTenantId,
				})
			)) as {
				data: { freelancersById: { items: ReduxFreelancers[] } }
			}

			if (
				Array.isArray(freelancerInfoArray) &&
				freelancerInfoArray.length === 1
			) {
				const freelancerInfo = freelancerInfoArray[0]
				return freelancerInfo
			}
			console.error(
				' Error with freelancerInfoArray: ',
				freelancerInfoArray
			)
			return null
		} catch (err: any) {
			return rejectWithValue(err)
		}
	}
)

// Can be called from both clients/:clientsId & project/:projectId
// On client page only companyAdmin can access
// On project companyAdmin & freelancer can access
export const createProject = createAsyncThunk<
	void,
	{
		parentClientId: string | null
		parentProjectId: string | null
		projectName: string
		projectGroup?: string | null
		allowProjectNumber?: boolean
		isSubTeam?: boolean
	},
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'project/createProject',
	async (
		{
			parentClientId = null,
			parentProjectId = null,
			allowProjectNumber,
			projectName,
			projectGroup,
			isSubTeam = false,
		},
		{ getState, rejectWithValue, dispatch }
	) => {
		try {
			// Get the current company info
			// if currentCompanyInfo doesn't exist or allowProjectNumber is false then we don't need to generate project number
			// else we need to generate project number
			// Then we check for if we need to add parentClientId or parentProjectId
			const { auth } = getState()
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('User Table Info is not set')
			}
			const inFreelancerArchive =
				auth.userTableInfo?.currentCompanyTenantId ===
					auth.userTableInfo?.tenantId &&
				auth.userTableInfo?.cognitoGroup?.includes('freelancer')
			let oldCompanyInfoDetails: ReduxCompany | null = null
			if (!inFreelancerArchive) {
				const { data, error } = await tryCatch(
					API.graphql(
						graphqlOperation(queries.getCompany, { id: tenantId })
					) as Promise<{ data: { getCompany: ReduxCompany } }>
				)
				if (error) {
					const errorMessage = getErrorMessage(error)
					console.log(
						'errorMessage in createProject & getCompany: ',
						errorMessage
					)
					return rejectWithValue(errorMessage)
				}
				oldCompanyInfoDetails = data?.data?.getCompany
				console.log('oldCompanyInfoDetails: ', oldCompanyInfoDetails)
			}
			// We return if project name or project group is empty

			if (!projectName || projectName.length === 0) {
				makeErrorToast('Project Name  cannot be empty')
				return rejectWithValue('Project Name cannot be empty')
			}
			// Base project details
			// still need to add project number and parentClientId or parentProjectId
			const newProjectDetails = {
				id: nanoid(),
				name: projectName,
				tenantId,
				projectGroup,
				activeFlag: true,
				type: 'project',
				freelancers: ['dummyId'],
				childFreelancers: ['dummyId'],
				offlineIds: ['dummyId'],
				isSubTeam,
			} as CreateProjectsInput
			if (
				oldCompanyInfoDetails &&
				allowProjectNumber &&
				!isSubTeam &&
				projectGroup
			) {
				// We look calculate project number and update company info
				function zeroPad(num: any, places: number) {
					return String(num).padStart(places, '0')
				}
				const { companyCount, newTypeCount, newTypeYear } =
					getProjectNumberDetails({
						// @ts-expect-error
						companyCount:
							oldCompanyInfoDetails.projectGroupCount &&
							oldCompanyInfoDetails.projectGroupCount.length >
								0 &&
							oldCompanyInfoDetails.projectGroupCount[0] !== null
								? oldCompanyInfoDetails.projectGroupCount
								: [],
						type: projectGroup,
					})
				const projectNumber = getProjectNumber({
					newTypeCount,
					newTypeYear,
					type: projectGroup,
					useOneProjectNumber:
						oldCompanyInfoDetails?.useOneProjectNumber ?? false,
					commonProjectNumber:
						oldCompanyInfoDetails?.commonProjectNumber ?? 0,
					// @ts-expect-error
					projectDetails:
						oldCompanyInfoDetails.projectGroupCount &&
						oldCompanyInfoDetails.projectGroupCount.length > 0 &&
						oldCompanyInfoDetails.projectGroupCount[0] !== null
							? oldCompanyInfoDetails.projectGroupCount
							: [],
					projectCustomisationDetails:
						oldCompanyInfoDetails.projectNumberCustomisation &&
						oldCompanyInfoDetails.projectNumberCustomisation
							.length > 0 &&
						oldCompanyInfoDetails.projectNumberCustomisation[0] !==
							null
							? oldCompanyInfoDetails.projectNumberCustomisation
							: [],
				})
				newProjectDetails.projectNumber = projectNumber

				// We update the company details to account for change in projectGroupCount
				const updatedCompanyInfoDetails: Partial<ReduxCompany> = {
					id: oldCompanyInfoDetails?.id,
					projectGroupCount: companyCount,
				}

				if (oldCompanyInfoDetails?.useOneProjectNumber) {
					updatedCompanyInfoDetails.commonProjectNumber =
						Number.parseInt(
							zeroPad(
								(oldCompanyInfoDetails.commonProjectNumber ||
									0) + 1,
								4
							)
						)
				}
				await updateCompanyInfoWithDeltaSyncSuppress(
					updatedCompanyInfoDetails
				)
			}

			if (parentClientId) {
				newProjectDetails.parentClientId = parentClientId
				newProjectDetails.parentTeamId = parentClientId
				// If there is a parent Client id then the project by itself is a sub team
				// so we set the parentSubTeamId to be the same as the project id
				newProjectDetails.parentSubTeamId = newProjectDetails.id
				await API.graphql(
					graphqlOperation(mutations.createProjects, {
						input: newProjectDetails,
					})
				)

				dispatch(
					projectApi.util.invalidateTags([
						{ type: 'Projects', id: `${parentClientId}-projects` },
					])
				)
			} else if (parentProjectId) {
				const {
					data: { getProjects: parentProjectData },
				} = (await API.graphql(
					graphqlOperation(queries.getProjects, {
						id: parentProjectId,
					})
				)) as { data: GetProjectsQueryApi }
				if (!parentProjectData) {
					Sentry.captureMessage(
						`parentProjectData not found while creating child project, parentProjectId: ${parentProjectId} `
					)
					return rejectWithValue('parentProjectData not found')
				}
				newProjectDetails.parentProjectId = parentProjectId
				newProjectDetails.parentTeamId = parentProjectData?.parentTeamId
				newProjectDetails.parentSubTeamId =
					parentProjectData?.parentSubTeamId
				newProjectDetails.tenantId = parentProjectData?.tenantId
				newProjectDetails.childFreelancers = [
					...(parentProjectData?.childFreelancers || ['dummyId']),
				]
				await API.graphql(
					graphqlOperation(mutations.createProjects, {
						input: newProjectDetails,
					})
				)

				dispatch(
					projectApi.util.invalidateTags([
						{ type: 'Projects', id: `${parentProjectId}-children` },
					])
				)
			} else {
				makeErrorToast(
					'parentClientId or parentProjectId is not defined'
				)
				return rejectWithValue(
					'parentClientId or parentProjectId is not defined'
				)
			}

			makeSuccessToast('Project Created Successfully')

			return
		} catch (err) {
			const errorMessage = getErrorMessage(err)
			makeErrorToast(errorMessage)
			return rejectWithValue(errorMessage)
		}
	}
)

//  Logic to add freelancer data
export const addFreelancerToProject = createAsyncThunk<
	void,
	{ freelanceEmail: string },
	{ state: RootState; dispatch: ThunkAppDispatch; rejectValue: string }
>(
	'project/addFreelancerToProject',
	async ({ freelanceEmail }, { getState, dispatch, rejectWithValue }) => {
		try {
			const { projects, auth } = getState()
			const currentProject = projects.currentProject
			const projectId = currentProject?.id
			const freelancerPage = projects.freelancerPage

			// Ensure the current user has a company tenant id.
			const currentCompanyTenantId =
				auth.userTableInfo?.currentCompanyTenantId
			if (!currentCompanyTenantId || !projectId) {
				return rejectWithValue('Auth error')
			}
			// Convert email to lowercase for consistent querying.
			const lowerEmail = freelanceEmail.trimEnd().toLowerCase()

			// Fetch freelancer info by email and filter by cognitoGroup.
			const {
				data: {
					userInfoByEmail: { items: freelancerDbData },
				},
			} = (await API.graphql(
				graphqlOperation(queries.userInfoByEmail, {
					email: lowerEmail,
					filter: { cognitoGroup: { contains: 'freelancer' } },
				})
			)) as {
				data: {
					userInfoByEmail: { items: ReduxUserInfo[] }
				}
			}

			if (!freelancerDbData || freelancerDbData.length === 0) {
				// if no freelancer is found we create a new one and send an invite to the user.
				await dispatch(
					userInfoApi.endpoints.addNewUser.initiate({
						email: lowerEmail,
						cognitoGroup: 'freelancer',
						tenantId: currentCompanyTenantId,
						projectIds: [projectId],
					})
				)
				makeSuccessToast('Freelancer Successfully Added')
			} else {
				// Use the first freelancer found.
				const freelancer = freelancerDbData[0]
				const freelancerTenantId = freelancer.tenantId
				const tErrors = i18next.getFixedT(null, 'errors')

				if (!freelancerTenantId) {
					// If freelancer record does not contain a tenantId, show error and exit.
					makeErrorToastWithLink({
						url: '/signupfreelancer',
						text: `${freelanceEmail} ${tErrors('addFreelancer')}`,
					})
					return rejectWithValue(
						'The User with the given email ID was not found!'
					)
				}

				if (
					currentProject?.freelancers?.includes(freelancerTenantId) ||
					currentProject?.childFreelancers?.includes(
						freelancerTenantId
					)
				) {
					// Check if freelancer is already added to the current project.
					makeErrorToast('User is already added to the Project')
					return rejectWithValue(
						'User is already added to the Project'
					)
				}

				// Prepare updated project IDs by combining existing ones with the parent project.
				const existingProjectIds = freelancer.projectIds ?? []
				const combinedProjectIds = [
					...existingProjectIds,
					projectId,
				].filter(Boolean) // Remove falsy values.
				const uniqueProjectIds = Array.from(new Set(combinedProjectIds))

				// If the freelancer's companyTenantId does not include the current user's companyTenantId,
				// include it in the update.
				const freelancerCompanyTenantIds =
					freelancer.companyTenantId ?? []
				if (
					!freelancerCompanyTenantIds.includes(currentCompanyTenantId)
				) {
					const updatedCompanyTenantIds = Array.from(
						new Set([
							...freelancerCompanyTenantIds,
							currentCompanyTenantId,
						])
					)

					await API.graphql(
						graphqlOperation(mutations.updateUserInfo, {
							input: {
								id: freelancer.id,
								projectIds: uniqueProjectIds,
								companyTenantId: updatedCompanyTenantIds,
							},
						})
					)
				} else {
					await API.graphql(
						graphqlOperation(mutations.updateUserInfo, {
							input: {
								id: freelancer.id,
								projectIds: uniqueProjectIds,
							},
						})
					)
				}

				// Call custom resolver to add the freelancer to the project.
				await API.graphql(
					graphqlOperation(mutations.addFreelancerToProject, {
						projectId,
						freelancerId: freelancerTenantId,
					})
				)
			}

			if (freelancerPage === 'parent') {
				if (currentProject?.parentClientId) {
					// Refresh parent client project list via RTK Query
					dispatch(
						projectApi.util.invalidateTags([
							{
								type: 'Projects',
								id: `${currentProject.parentClientId}-projects`,
							},
						])
					)
				} else if (currentProject?.parentProjectId) {
					dispatch(
						projectApi.util.invalidateTags([
							{
								type: 'Projects',
								id: `${currentProject.parentProjectId}-children`,
							},
						])
					)
				}
			} else if (freelancerPage === 'child') {
				dispatch(
					projectApi.util.invalidateTags([
						{ type: 'Projects', id: `${projectId}-children` },
					])
				)
				dispatch(
					projectApi.util.invalidateTags([
						{ type: 'Projects', id: projectId },
					])
				)
			}
			const {
				data: { getProjects: projectData },
			} = (await API.graphql(
				graphqlOperation(queries.getProjects, {
					id: projectId,
				})
			)) as {
				data: GetProjectsQueryApi
			}
			dispatch(setUpdateCurrentProject(projectData))

			makeSuccessToast('Freelancer Successfully Added')
			return
		} catch (err: any) {
			// If the error is a timeout, treat it as a success.
			if (err?.errors?.[0]?.errorType === 'ExecutionTimeout') {
				makeSuccessToast('Freelancer Successfully Added')
				return
			}
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const updateProject = createAsyncThunk<
	void,
	{ project: ReduxProjects; projectName: string; projectNumber?: string },
	{ state: RootState; dispatch: ThunkAppDispatch; rejectValue: string }
>(
	'project/updateProject',
	async (
		{ project, projectName, projectNumber },
		{ dispatch, getState, rejectWithValue }
	) => {
		const tProjectChildren = i18next.getFixedT(null, 'projectChildren')
		// Gets the original Project from datastore then updates it
		try {
			const { auth } = getState()
			const filter = getFetchFilter(auth.userTableInfo)
			const {
				data: { getProjects: originalProject },
			} = (await API.graphql(
				graphqlOperation(queries.getProjects, {
					id: project.id,
					filter,
				})
			)) as { data: GetProjectsQueryApi }
			// We check if we have access to the project
			if (originalProject && project.id) {
				const tProjectChildren = i18next.getFixedT(
					null,
					'projectChildren'
				)
				const projectUpdateParams: UpdateProjectsInput = {
					id: project.id,
					name: projectName,
				}

				// Include projectNumber if provided
				if (projectNumber !== undefined) {
					projectUpdateParams.projectNumber = projectNumber
				}

				await API.graphql(
					graphqlOperation(mutations.updateProjects, {
						input: projectUpdateParams,
					})
				)
			} else {
				rejectWithValue(`Can't access project`)
			}

			if (project.isSubTeam) {
				dispatch(
					projectApi.util.invalidateTags([
						{ type: 'Projects', id: 'LIST' },
					])
				)
				makeSuccessToast(tProjectChildren('subTeam.successMsg:update'))
			} else {
				makeSuccessToast(tProjectChildren('project.successMsg:update'))
			}

			if (project.parentClientId) {
				dispatch(
					projectApi.util.invalidateTags([
						{
							type: 'Projects',
							id: `${project.parentClientId}-projects`,
						},
					])
				)
				return
			}

			if (project.parentProjectId) {
				dispatch(
					projectApi.util.invalidateTags([
						{
							type: 'Projects',
							id: `${project.parentProjectId}-children`,
						},
					])
				)
				return
			}
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const deleteProject = createAsyncThunk<
	void,
	{ project: ReduxProjects },
	{
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'project/deleteProject',
	async ({ project }, { dispatch, rejectWithValue }) => {
		// Gets the original Project from datastore then deletes it along with all it's children project & quotes
		try {
			await API.graphql(
				graphqlOperation(mutations.deleteProjectById, {
					projectId: project.id,
				})
			)
			if (project.parentClientId) {
				dispatch(
					projectApi.util.invalidateTags([
						{
							type: 'Projects',
							id: `${project.parentClientId}-projects`,
						},
					])
				)
			}
			if (project.parentProjectId) {
				dispatch(
					projectApi.util.invalidateTags([
						{
							type: 'Projects',
							id: `${project.parentProjectId}-children`,
						},
					])
				)
			}
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const createFreelancerArchiveProject = createAsyncThunk<
	void,
	UserInfo,
	{
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'project/createFreelancerArchiveProject',
	async (userInfo, { dispatch, rejectWithValue }) => {
		try {
			const items = await fetchArchiveProjectsByTenantIdUtil(
				userInfo.tenantId
			)

			if (
				userInfo?.groups?.includes('freelancer') &&
				items.length === 0
			) {
				const projectDetails = {
					id: nanoid(),
					name: 'My Projects',
					tenantId: userInfo.tenantId,
					activeFlag: true,
					type: 'archive',
					freelancers: ['dummyId', userInfo.tenantId],
					childFreelancers: ['dummyId', userInfo.tenantId],
					offlineIds: ['dummyId'],
				}
				await API.graphql(
					graphqlOperation(mutations.createProjects, {
						input: projectDetails,
					})
				)
				await delayInMs(2500)
				dispatch(
					projectApi.util.invalidateTags([
						{ type: 'Projects', id: 'LIST' },
					])
				)
			}
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

//  Logic to delete freelancer data
export const removeFreelancerFromProject = createAsyncThunk<
	void,
	{ projectId: string; freelancerId: string },
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'project/removeFreelancerFromProject',
	async (
		{ projectId, freelancerId },
		{ dispatch, getState, rejectWithValue }
	) => {
		const tDeleteFreelancer = i18next.getFixedT(null, 'DeleteFreelancer')
		try {
			const { projects, auth } = getState()
			const freelancerPage = projects.freelancerPage
			const currentProject = projects.currentProject
			const filter = getFetchFilter(auth.userTableInfo)
			// Delete freelancer from project with api call
			await API.graphql(
				graphqlOperation(mutations.removeFreelancerFromProject, {
					projectId,
					freelancerId,
				})
			)

			const {
				data: {
					userInfoByTenantId: { items: freelancerDbData },
				},
			} = (await API.graphql(
				graphqlOperation(queries.userInfoByTenantId, {
					tenantId: freelancerId,
				})
			)) as {
				data: {
					userInfoByTenantId: { items: ReduxUserInfo[] }
				}
			}
			const projectIds = freelancerDbData?.[0]?.projectIds?.filter(
				(p) => p !== projectId
			)

			await API.graphql(
				graphqlOperation(mutations.updateUserInfo, {
					input: {
						id: freelancerDbData[0].id,
						projectIds,
					},
				})
			)

			if (freelancerPage === 'parent') {
				if (currentProject?.parentClientId) {
					// Dispatch a refresh for the project based on parent data.
					dispatch(
						fetchProject({
							parentClientId: currentProject.parentClientId,
						})
					)
				} else if (currentProject?.parentProjectId) {
					// Invalidate projectChildren cache so RTK Query refetches
					dispatch(
						projectApi.util.invalidateTags([
							{
								type: 'Projects',
								id: `${currentProject.parentProjectId}-children`,
							},
						])
					)
				}
			} else if (freelancerPage === 'child') {
				// Invalidate projectChildren cache so RTK Query refetches
				dispatch(
					projectApi.util.invalidateTags([
						{ type: 'Projects', id: `${projectId}-children` },
					])
				)
				dispatch(
					projectApi.util.invalidateTags([
						{ type: 'Projects', id: projectId },
					])
				)
			}
			const tDeleteFreelancer = i18next.getFixedT(
				null,
				'DeleteFreelancer'
			)
			const {
				data: { getProjects: projectData },
			} = (await API.graphql(
				graphqlOperation(queries.getProjects, {
					id: projectId,
				})
			)) as {
				data: GetProjectsQueryApi
			}
			dispatch(setUpdateCurrentProject(projectData))

			makeSuccessToast(tDeleteFreelancer('removedFreelancer'))
			await dispatch(listFreelancerInfo())
		} catch (err: any) {
			if (err?.errors?.[0]?.errorType === 'ExecutionTimeout') {
				makeSuccessToast(tDeleteFreelancer('removedFreelancer'))
				return
			}
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const moveProject = createAsyncThunk<
	void,
	{
		projectId: string
		newParentProjectId?: string
		newParentClientId?: string
	},
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'project/moveProject',
	async (
		{ projectId, newParentProjectId = null, newParentClientId = null },
		{ dispatch, getState, rejectWithValue }
	) => {
		if (projectId === newParentProjectId) {
			makeErrorToast(msg)
			return rejectWithValue('Try a different project to move to')
		}
		const tProjectChildren = i18next.getFixedT(null, 'projectChildren')
		try {
			const { auth } = getState()
			const {
				data: { getProjects: currentProjects },
			} = (await API.graphql(
				graphqlOperation(queries.getProjects, { id: projectId })
			)) as {
				data: GetProjectsQueryApi
			}
			const accessibleProject: ReduxProjects | null =
				checkIfItemIsAccessible(auth.userTableInfo, currentProjects)
			if (!accessibleProject) {
				makeErrorToast(msg)
				return rejectWithValue('Error Fetching Project')
			}
			let projectUpdateParams = {} as UpdateProjectsInput
			if (newParentProjectId) {
				const {
					data: { getProjects: parentProject },
				} = (await API.graphql(
					graphqlOperation(queries.getProjects, {
						id: newParentProjectId,
					})
				)) as {
					data: GetProjectsQueryApi
				}
				projectUpdateParams = {
					id: projectId,
					parentProjectId: newParentProjectId,
					parentClientId: null,
					parentTeamId: parentProject?.parentTeamId,
					parentSubTeamId: parentProject?.parentSubTeamId,
					childFreelancers: union(
						parentProject?.childFreelancers,
						currentProjects?.childFreelancers
					),
				}
			} else if (newParentClientId) {
				projectUpdateParams = {
					id: projectId,
					parentProjectId: null,
					parentClientId: newParentClientId,
					parentTeamId: newParentClientId,
					parentSubTeamId: projectId,
					childFreelancers: currentProjects?.childFreelancers,
				}
			}
			await API.graphql(
				graphqlOperation(mutations.updateProjects, {
					input: projectUpdateParams,
				})
			)
			await API.graphql(
				graphqlOperation(mutations.moveProject, {
					projectId: projectId,
					tenantId: currentProjects?.tenantId,
					parentTeamId: projectUpdateParams.parentTeamId,
					parentSubTeamId: projectUpdateParams.parentSubTeamId,
					childFreelancers: projectUpdateParams.childFreelancers,
				})
			)

			if (currentProjects?.parentClientId) {
				dispatch(
					projectApi.util.invalidateTags([
						{
							type: 'Projects',
							id: `${currentProjects?.parentClientId}-projects`,
						},
					])
				)
			} else {
				dispatch(
					projectApi.util.invalidateTags([
						{
							type: 'Projects',
							id: `${accessibleProject.parentProjectId as string}-children`,
						},
					])
				)
			}

			makeSuccessToast(tProjectChildren('project:successMsg:move'))
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const projectSlice = createSlice({
	name: 'project',
	initialState,
	reducers: {
		setUpdateCurrentProject: (state, action) => {
			state.currentProject = action.payload
		},
		setIsDeleteDialogOpen: (state, action) => {
			state.isDeleteDialogOpen = action.payload
		},
		setSummaryQuoteId: (state, action) => {
			state.summaryQuoteId = action.payload
		},
		setQuoteType: (state, action) => {
			state.quoteType = action.payload
		},
		setProjectModalOpenStatus: (state, action) => {
			state.projectModalOpenStatus = action.payload.status
			state.currentProject = action.payload.project
		},
		setSubTeamModalOpenStatus: (state, action) => {
			state.subTeamModalOpenStatus = action.payload.status
			state.currentProject = action.payload.project
		},
		setSubTeamDeleteDialogOpenStatus: (state, action) => {
			state.subTeamDeleteDialogOpenStatus = action.payload.status
			state.currentProject = action.payload.project
		},
		setIsFreelancersModalOpen: (state, action) => {
			state.isFreelancerModalOpen = action.payload.status
			state.currentProject = action.payload.project
			state.freelancerPage = action.payload.page
		},
		setProjectDeleteDialogOpenStatus: (state, action) => {
			state.projectDeleteDialogOpenStatus = action.payload.status
			state.currentProject = action.payload.project
		},
		setProjectMoveDialogOpenStatus: (state, action) => {
			state.projectMoveDialogOpenStatus = action.payload
		},
		sortProjects: (state, action) => {
			if (action.payload === 'date') {
				state.projectData = orderBy(
					state.projectData,
					['createdOn'],
					['asc']
				)
			} else if (action.payload === 'dateM') {
				state.projectData = orderBy(
					state.projectData,
					['createdOn'],
					['desc']
				)
			} else if (action.payload === 'name') {
				state.projectData = orderBy(
					state.projectData,
					[(project) => project?.name?.toLowerCase()],
					['asc']
				)
			}
		},
		sortProjectChildren: (state, action) => {
			if (action.payload === 'date') {
				state.projectChildrenData = orderBy(
					state.projectChildrenData,
					['createdOn'],
					['asc']
				)
			} else if (action.payload === 'dateM') {
				state.projectChildrenData = orderBy(
					state.projectChildrenData,
					['createdOn'],
					['desc']
				)
			} else if (action.payload === 'name') {
				state.projectChildrenData = orderBy(
					state.projectChildrenData,
					[(projectChild) => projectChild?.name?.toLowerCase()],
					['asc']
				)
			}
		},
		sortAndMergeProjectChildren: (state, action) => {
			const { projectChildren, sort } = action.payload
			let sortedProjectChildren = projectChildren
			if (sort === 'date') {
				sortedProjectChildren =
					projectChildrenMergeSort(projectChildren)
			} else if (sort === 'name') {
				sortedProjectChildren = orderBy(
					projectChildren,
					[(projectChild) => projectChild?.name?.toLowerCase()],
					['asc']
				)
			}
			state.projectChildrenData = sortedProjectChildren
		},
		clearProjectsData: (state) => {
			state.projectData = []
			state.projectChildrenData = []
		},
		clearProjectsChildrenData: (state) => {
			state.projectChildrenData = []
		},
		setFetchProjectApiStatus: (state, action) => {
			state.fetchProjectChildrenApiStatus = action.payload
		},
		setFreelancerChargeConfirmDialogStatus: (state, action) => {
			state.freelancerChargeConfirmDialogStatus = action.payload
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchAllProjects.pending, (state, action) => {
			state.fetchAllProjectApiStatus = 'loading'
		})
		builder.addCase(fetchAllProjects.fulfilled, (state, action) => {
			state.fetchAllProjectApiStatus = 'succeeded'
			state.allProjects = action.payload
		})
		builder.addCase(fetchAllProjects.rejected, (state, action) => {
			state.fetchAllProjectApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(fetchProject.pending, (state, action) => {
			state.fetchProjectApiStatus = 'loading'
		})
		builder.addCase(fetchProject.fulfilled, (state, action) => {
			state.fetchProjectApiStatus = 'succeeded'
			state.projectData = action.payload
		})
		builder.addCase(fetchProject.rejected, (state, action) => {
			state.fetchProjectApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(fetchFreelancerProject.pending, (state, action) => {
			state.fetchProjectApiStatus = 'loading'
		})
		builder.addCase(fetchFreelancerProject.fulfilled, (state, action) => {
			state.fetchProjectApiStatus = 'succeeded'
			state.projectData = action.payload
		})
		builder.addCase(fetchFreelancerProject.rejected, (state, action) => {
			state.fetchProjectApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(fetchProjectChildren.pending, (state, action) => {
			state.fetchProjectChildrenApiStatus = 'loading'
		})
		builder.addCase(fetchProjectChildren.fulfilled, (state, action) => {
			state.fetchProjectChildrenApiStatus = 'succeeded'
			state.projectChildrenData = action.payload
		})
		builder.addCase(fetchProjectChildren.rejected, (state, action) => {
			state.fetchProjectChildrenApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(fetchProjectTotals.pending, (state, action) => {
			state.fetchProjectTotalsApiStatus = 'loading'
		})
		builder.addCase(fetchProjectTotals.fulfilled, (state, action) => {
			state.fetchProjectTotalsApiStatus = 'succeeded'
			state.projectTotals = action.payload
		})
		builder.addCase(fetchProjectTotals.rejected, (state, action) => {
			state.fetchProjectTotalsApiStatus = 'failed'
			state.projectTotals = null
			state.error = action.error.message
		})
		builder.addCase(getProjectTotalsFromLambda.pending, (state, action) => {
			state.getProjectTotalsFromLambdaApiStatus = 'loading'
		})
		builder.addCase(
			getProjectTotalsFromLambda.fulfilled,
			(state, action) => {
				state.getProjectTotalsFromLambdaApiStatus = 'succeeded'
				state.projectTotalsLambda = action.payload
			}
		)
		builder.addCase(
			getProjectTotalsFromLambda.rejected,
			(state, action) => {
				state.getProjectTotalsFromLambdaApiStatus = 'failed'
				state.projectTotalsLambda = null
				state.error = action.error.message
			}
		)
		builder.addCase(listFreelancerInfo.pending, (state, action) => {
			state.listFreelancerInfoApiStatus = 'loading'
		})
		builder.addCase(listFreelancerInfo.fulfilled, (state, action) => {
			state.listFreelancerInfoApiStatus = 'succeeded'
			state.freelanceEmail = [...action.payload]
		})
		builder.addCase(fetchFreelancerInfo.pending, (state, action) => {
			state.fetchFreelancerInfoApiStatus = 'loading'
		})
		builder.addCase(fetchFreelancerInfo.fulfilled, (state, action) => {
			state.freelancerInfo = action.payload
			state.fetchFreelancerInfoApiStatus = 'succeeded'
		})
		builder.addCase(createProject.pending, (state, action) => {
			state.createOrUpdateProjectApiStatus = 'loading'
		})
		builder.addCase(createProject.fulfilled, (state, action) => {
			state.createOrUpdateProjectApiStatus = 'succeeded'
			state.projectModalOpenStatus = false
			state.subTeamModalOpenStatus = false
		})
		builder.addCase(createProject.rejected, (state, action) => {
			state.createOrUpdateProjectApiStatus = 'failed'
			state.projectModalOpenStatus = false
			state.subTeamModalOpenStatus = false
			state.error = action.error?.message
			if (action.payload) {
				state.error = action.payload
			}
		})
		builder.addCase(updateProject.pending, (state, action) => {
			state.createOrUpdateProjectApiStatus = 'loading'
		})
		builder.addCase(updateProject.fulfilled, (state, action) => {
			state.createOrUpdateProjectApiStatus = 'succeeded'
			state.projectModalOpenStatus = false
			state.subTeamModalOpenStatus = false
		})
		builder.addCase(updateProject.rejected, (state, action) => {
			state.createOrUpdateProjectApiStatus = 'failed'
			state.projectModalOpenStatus = false
			state.subTeamModalOpenStatus = false
			state.error = action.error?.message
			if (action.payload) {
				state.error = action.payload
			}
		})
		builder.addCase(deleteProject.pending, (state, action) => {
			state.deleteProjectApiStatus = 'loading'
		})
		builder.addCase(deleteProject.fulfilled, (state, action) => {
			state.deleteProjectApiStatus = 'succeeded'
			state.projectDeleteDialogOpenStatus = false
		})
		builder.addCase(deleteProject.rejected, (state, action) => {
			state.deleteProjectApiStatus = 'failed'
			state.projectDeleteDialogOpenStatus = false
			state.error = action.error.message
		})
		builder.addCase(
			createFreelancerArchiveProject.pending,
			(state, action) => {
				state.createFreelancerArchiveProjectApiStatus = 'loading'
			}
		)
		builder.addCase(
			createFreelancerArchiveProject.fulfilled,
			(state, action) => {
				state.createFreelancerArchiveProjectApiStatus = 'succeeded'
			}
		)
		builder.addCase(
			createFreelancerArchiveProject.rejected,
			(state, action) => {
				state.deleteProjectApiStatus = 'failed'
				state.error = action.error.message
			}
		)
		builder.addCase(addFreelancerToProject.pending, (state, action) => {
			state.updateFreelancerInfoStatus = 'loading'
		})
		builder.addCase(addFreelancerToProject.fulfilled, (state, action) => {
			state.updateFreelancerInfoStatus = 'succeeded'
			state.isFreelancerModalOpen = false
			state.freelancerPage = null
		})
		builder.addCase(addFreelancerToProject.rejected, (state, action) => {
			state.updateFreelancerInfoStatus = 'failed'
			state.isFreelancerModalOpen = false
			state.freelancerPage = null
			state.error = action.error.message
		})
		builder.addCase(
			removeFreelancerFromProject.pending,
			(state, action) => {
				state.removeFreelancerStatus = 'loading'
			}
		)
		builder.addCase(
			removeFreelancerFromProject.fulfilled,
			(state, action) => {
				state.removeFreelancerStatus = 'succeeded'
				state.isDeleteDialogOpen = false
			}
		)
		builder.addCase(moveProject.pending, (state, action) => {
			state.moveProjectApiStatus = 'loading'
		})
		builder.addCase(moveProject.fulfilled, (state, action) => {
			state.moveProjectApiStatus = 'succeeded'
			state.currentProject = null
			state.projectMoveDialogOpenStatus = false
		})
		builder.addCase(moveProject.rejected, (state, action) => {
			state.moveProjectApiStatus = 'rejected'
			state.projectMoveDialogOpenStatus = false
			state.error = action.error?.message
			if (action.payload) {
				state.error = action.payload
			}
		})
		builder.addMatcher(
			projectApi.endpoints.createSubTeamById.matchFulfilled,
			(state, action) => {
				state.createOrUpdateProjectApiStatus = 'succeeded'
				state.subTeamModalOpenStatus = false
			}
		)
		builder.addMatcher(
			projectApi.endpoints.createSubTeamById.matchRejected,
			(state, action) => {
				state.createOrUpdateProjectApiStatus = 'failed'
				state.subTeamModalOpenStatus = false
			}
		)
		builder.addMatcher(
			projectApi.endpoints.updateSubTeamById.matchFulfilled,
			(state, action) => {
				state.createOrUpdateProjectApiStatus = 'succeeded'
				state.subTeamModalOpenStatus = false
			}
		)
		builder.addMatcher(
			projectApi.endpoints.updateSubTeamById.matchRejected,
			(state, action) => {
				state.createOrUpdateProjectApiStatus = 'failed'
				state.subTeamModalOpenStatus = false
			}
		)
		builder.addMatcher(
			projectApi.endpoints.fetchProjectChildren.matchFulfilled,
			(state, action) => {
				state.fetchProjectChildrenApiStatus = 'succeeded'
				state.projectChildrenData = action.payload || []
			}
		)
		builder.addMatcher(
			projectApi.endpoints.fetchProjectChildren.matchPending,
			(state) => {
				state.fetchProjectChildrenApiStatus = 'loading'
				state.projectChildrenData = []
			}
		)
		builder.addMatcher(
			projectApi.endpoints.fetchProjectChildren.matchRejected,
			(state, action) => {
				state.fetchProjectChildrenApiStatus = 'failed'
				state.error = action.error.message
			}
		)
		builder.addMatcher(
			projectApi.endpoints.fetchProject.matchFulfilled,
			(state, action) => {
				state.fetchProjectApiStatus = 'succeeded'
				state.projectData = action.payload || []
			}
		)
		builder.addMatcher(
			projectApi.endpoints.fetchProject.matchRejected,
			(state, action) => {
				state.fetchProjectApiStatus = 'failed'
				state.error = action.error.message
			}
		)
		builder.addMatcher(
			projectApi.endpoints.fetchProject.matchFulfilled,
			(state, action) => {
				state.fetchProjectApiStatus = 'succeeded'
				state.projectData = action.payload || []
			}
		)
		builder.addMatcher(
			projectApi.endpoints.fetchProject.matchRejected,
			(state, action) => {
				state.fetchProjectApiStatus = 'failed'
				state.error = action.error.message
			}
		)
	},
})

export const {
	setUpdateCurrentProject,
	setIsDeleteDialogOpen,
	setProjectModalOpenStatus,
	setSubTeamModalOpenStatus,
	setProjectDeleteDialogOpenStatus,
	setSubTeamDeleteDialogOpenStatus,
	sortProjects,
	sortProjectChildren,
	sortAndMergeProjectChildren,
	setSummaryQuoteId,
	setQuoteType,
	setIsFreelancersModalOpen,
	setProjectMoveDialogOpenStatus,
	clearProjectsData,
	clearProjectsChildrenData,
	setFetchProjectApiStatus,
	setFreelancerChargeConfirmDialogStatus,
} = projectSlice.actions

export default projectSlice.reducer as Reducer<ProjectState>
